import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CorreoTab, MarcaTab, SmtpTab, TelegramTab } from "@/components/ajustes/BasicTabs";
import { WhatsappTab } from "@/components/ajustes/WhatsappTab";
import { AiInstructions, AiModelsPanel, AiUsagePanel } from "@/components/ajustes/AiPanels";
import { DigestPanel, ReportsPanel } from "@/components/ajustes/DigestPanels";
import {
  AutoArchivePanel, CapturesPanel, CloudMediaPanel, WpResyncPanel,
} from "@/components/ajustes/MantenimientoPanels";
import { api, errMsg, fetcher } from "@/lib/api";

const PANEL = "border border-border bg-card p-6 mt-4 max-w-2xl";

export default function Ajustes() {
  const { data, mutate } = useSWR("/settings", fetcher);
  const { data: models } = useSWR("/ai/models", fetcher);
  const { data: usage } = useSWR("/ai/usage?days=30", fetcher, { refreshInterval: 60000 });
  const { data: runs, mutate: mutateRuns } = useSWR("/digest/runs", fetcher);
  const { data: outlets } = useSWR("/outlets", fetcher);
  const { data: shots, mutate: mutateShots } = useSWR("/docs/screenshots/status", fetcher,
    { refreshInterval: 10000 });
  const { data: media, mutate: mutateMedia } = useSWR("/media/migrate/status", fetcher,
    { refreshInterval: 15000 });
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [resyncLimit, setResyncLimit] = useState(30);
  const [resync, setResync] = useState(null);
  const [archive, setArchive] = useState(null);
  const [report, setReport] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [weeklyPreview, setWeeklyPreview] = useState("");

  const cheapest = useMemo(() => (usage?.models?.length
    ? [...usage.models].sort((a, b) => a.cost_per_call - b.cost_per_call)[0]
    : null), [usage]);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const sendWeeklyNow = () => run("weekly", async () => {
    const { data: r } = await api.post("/digest/weekly-report/run-now");
    setWeekly(r);
    r.ran ? toast.success("Parte semanal enviado") : toast.info(r.reason);
  });

  const previewWeekly = () => run("weekly-preview", async () => {
    const { data: r } = await api.get("/digest/weekly-report/preview?weeks_back=1");
    setWeeklyPreview(r.text || "Sin datos");
  });

  const sendReportNow = () => run("report", async () => {
    const { data: r } = await api.post("/digest/newsroom-report/run-now");
    setReport(r);
    r.ran ? toast.success("Parte enviado") : toast.info(r.reason);
  });

  const archiveNow = () => run("archive", async () => {
    const { data: r } = await api.post("/ingest/auto-archive/run-now");
    setArchive(r);
    r.ran ? toast.success(`${r.archived} nota(s) archivadas`) : toast.info(r.reason);
  });

  const sendSummaryNow = () => run("summary", async () => {
    const { data: r } = await api.post("/digest/messaging-summary/run-now");
    if (!r.ran) return toast.info(r.reason);
    const enviados = (r.results || []).reduce((n, x) => n + (x.sent || 0), 0);
    toast.success(`Resumen enviado a ${enviados} destino(s)`);
    (r.results || []).forEach((x) =>
      (x.errors || []).forEach((e) => toast.error(`${x.outlet} · ${e.channel}: ${e.error}`)));
  });

  const runNow = () => run("run", async () => {
    const { data: r } = await api.post("/digest/run-now");
    if (!r.ran) toast.info(r.reason);
    else {
      const total = (r.results || []).reduce((n, x) => n + (x.sent || 0), 0);
      toast.success(`Boletines enviados: ${total} correos`);
    }
    mutateRuns();
  });

  const runCaptures = async (scope) => {
    setBusy(`shots-${scope}`);
    try {
      const { data: job } = await api.post("/docs/screenshots/run", { scope });
      if (job.already) toast.info("Ya hay una actualización de capturas en curso");
      else toast.success(`Actualizando ${job.total} captura(s)…`);
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: st } = await api.get(
          `/docs/screenshots/status?job_id=${encodeURIComponent(job.job_id)}`);
        mutateShots(st, false);
        if (st.state !== "running") {
          st.state === "error"
            ? toast.error(`No se pudo capturar: ${st.error}`)
            : toast.success(`Capturas listas: ${st.ok} al día · ${st.failed} con error`);
          break;
        }
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
      mutateShots();
    }
  };

  const migrateMedia = async () => {
    setBusy("media");
    try {
      const { data: job } = await api.post("/media/migrate", { limit: 900 });
      toast.success(`Migración iniciada (${job.pending} imágenes)`);
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: st } = await api.get(`/media/migrate/status?job_id=${encodeURIComponent(job.job_id)}`);
        mutateMedia(st, false);
        if (st.state !== "running") break;
      }
      toast.success("Imágenes movidas a la nube");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
      mutateMedia();
    }
  };

  const resyncWp = async () => {
    setBusy("resync");
    setResync(null);
    try {
      const { data: job } = await api.post("/wp/resync-meta", { limit: resyncLimit });
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await api.get(`/wp/resync-meta/${encodeURIComponent(job.job_id)}`);
        setResync(st);
        if (st.state !== "running") break;
      }
      toast.success("Reparación terminada");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  useEffect(() => { if (data && !s) setS(data); }, [data, s]);
  if (!s) return <p className="text-sm text-muted-foreground">Cargando configuración…</p>;

  const sec = (section, k, v) => setS((p) => ({ ...p, [section]: { ...(p[section] || {}), [k]: v } }));

  const save = () => run("save", async () => {
    await api.put("/settings", s);
    toast.success("Configuración guardada");
    mutate();
  });

  const test = (service) => run(service, async () => {
    const { data: r } = await api.get(`/settings/test/${service}`);
    r.ok ? toast.success(r.detail) : toast.error(r.detail);
  });

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/whatsapp`;

  return (
    <div>
      <PageHeader
        kicker="Configuración"
        title="Integraciones y automatización"
        description="Credenciales de correo entrante, SMTP para boletines, Telegram, WhatsApp y el modelo de IA de redacción."
      >
        <Button onClick={save} disabled={busy === "save"} data-testid="save-settings-btn">
          <Save className="h-4 w-4 mr-2" />Guardar
        </Button>
      </PageHeader>

      <Tabs defaultValue="correo">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="correo" data-testid="tab-correo">Correo entrante</TabsTrigger>
          <TabsTrigger value="smtp" data-testid="tab-smtp">SMTP boletines</TabsTrigger>
          <TabsTrigger value="telegram" data-testid="tab-telegram">Telegram</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="ia" data-testid="tab-ia">IA y boletín</TabsTrigger>
          <TabsTrigger value="marca" data-testid="tab-marca">Institucional</TabsTrigger>
          <TabsTrigger value="mantenimiento" data-testid="tab-mantenimiento">Mantenimiento</TabsTrigger>
        </TabsList>

        <TabsContent value="correo" className={PANEL}>
          <CorreoTab s={s} sec={sec} busy={busy} test={test} />
        </TabsContent>

        <TabsContent value="smtp" className={PANEL}>
          <SmtpTab s={s} sec={sec} busy={busy} test={test} />
        </TabsContent>

        <TabsContent value="telegram" className={PANEL}>
          <TelegramTab s={s} sec={sec} busy={busy} test={test} />
        </TabsContent>

        <TabsContent value="whatsapp" className={PANEL}>
          <WhatsappTab s={s} sec={sec} busy={busy} test={test} outlets={outlets}
                       webhookUrl={webhookUrl} />
        </TabsContent>

        <TabsContent value="ia" className={PANEL}>
          <AiModelsPanel s={s} sec={sec} models={models} />
          <AiUsagePanel s={s} sec={sec} models={models} usage={usage} cheapest={cheapest} />
          <AiInstructions s={s} sec={sec} />
          <DigestPanel s={s} sec={sec} busy={busy} runs={runs} runNow={runNow}
                       sendSummaryNow={sendSummaryNow}>
            <ReportsPanel s={s} sec={sec} busy={busy} report={report} weekly={weekly}
                          weeklyPreview={weeklyPreview} sendReportNow={sendReportNow}
                          sendWeeklyNow={sendWeeklyNow} previewWeekly={previewWeekly} />
          </DigestPanel>
        </TabsContent>

        <TabsContent value="marca" className={PANEL}>
          <MarcaTab s={s} sec={sec} />
        </TabsContent>

        <TabsContent value="mantenimiento" className={PANEL}>
          <WpResyncPanel busy={busy} resync={resync} resyncLimit={resyncLimit}
                         setResyncLimit={setResyncLimit} resyncWp={resyncWp} />
          <CloudMediaPanel busy={busy} media={media} migrateMedia={migrateMedia} />
          <AutoArchivePanel s={s} sec={sec} busy={busy} archive={archive}
                            archiveNow={archiveNow} save={save} />
          <CapturesPanel busy={busy} shots={shots} runCaptures={runCaptures} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
